import React from 'react'
import { Printer, Text, Row, Line, Cut, render } from 'react-thermal-printer'
import { VoucherResponse } from '@/app/types/voucher'
import { Party } from '@/app/types/party'
import { CompanyDetails } from '@/app/services/thermalPrinterService'
import { format } from 'date-fns'
import { PartyClosingBalanceResponse } from '@/app/types/reports'

interface ThermalReceiptProps {
  invoice: VoucherResponse
  party?: Party
  company: CompanyDetails
  paperSize?: '80mm' | '58mm' | '44mm'
  printerType?: 'epson' | 'star'
}

const getCharactersPerLine = (paperSize: string): number => {
  switch (paperSize) {
    case '80mm':
      return 48
    case '58mm':
      return 36
    case '44mm':
      return 28
    default:
      return 48
  }
}

/**
 * Helper function to format site address from individual fields
 */
function formatSiteAddress(site: VoucherResponse['site']): { addressLine1: string; addressLine2: string } {
  if (!site) return { addressLine1: '', addressLine2: '' }

  const line1Parts = []
  if (site.address_line1) line1Parts.push(site.address_line1)
  if (site.address_line2) line1Parts.push(site.address_line2)

  const line2Parts = []
  if (site.city) line2Parts.push(site.city)
  if (site.state) line2Parts.push(site.state)
  if (site.postal_code) line2Parts.push(site.postal_code)

  return {
    addressLine1: line1Parts.join(', '),
    addressLine2: line2Parts.join(', ')
  }
}

// This component is kept for preview purposes only
export const ThermalReceipt: React.FC<ThermalReceiptProps> = ({
  invoice,
  party,
  company,
  paperSize = '80mm',
  printerType = 'epson'
}) => {
  // For preview, we just return a placeholder since the actual rendering
  // happens in the generateThermalReceipt function
  return <div>Thermal Receipt Component</div>
}

// Function to generate receipt data
export async function generateThermalReceipt(
  invoice: VoucherResponse,
  party: Party | undefined,
  company: CompanyDetails,
  paperSize: '80mm' | '58mm' | '44mm' = '80mm',
  printerType: 'epson' | 'star' = 'epson',
  closingBalance?: PartyClosingBalanceResponse
): Promise<Uint8Array> {
  const width = getCharactersPerLine(paperSize)

  // Get site address or fallback to company address
  const siteAddress = formatSiteAddress(invoice.site)
  const displayAddress1 = siteAddress.addressLine1 || company.address
  const displayAddress2 = siteAddress.addressLine2 || company.cityStatePin
  const displayPhone = invoice.site?.contact_number || company.phone
  const displayGstin = invoice.site?.gstin || company.gstin

  // Calculate totals
  let subtotal = 0
  let totalTax = 0
  let totalDiscount = 0
  let deliveryCharge = Number(invoice.delivery_charge || 0)
  let grandTotal = 0
  
  const taxBreakdown: { [key: number]: { amount: number, taxableAmount: number } } = {}
  
  invoice.items?.forEach((item) => {
    const quantity = Number(item.quantity || 0)
    const rate = Number(item.rate || 0)
    const discountAmount = Number(item.discount_amount || 0)
    const taxAmount = Number(item.tax_amount || 0)
    const totalAmount = Number(item.total_amount || item.amount || 0)
    const taxableAmount = Number(item.taxable_amount || 0)
    
    // For subtotal, use taxable amount + discount (before tax)
    subtotal += taxableAmount + discountAmount
    totalDiscount += discountAmount
    totalTax += taxAmount
    grandTotal += totalAmount
    
    // Group tax by rate using GST breakdown
    if (item.gst_breakdown && item.gst_breakdown.length > 0) {
      const totalRate = item.gst_breakdown.reduce((sum, gst) => {
        // Handle both 'rate' and 'rate_percent' fields
        const rate = gst.rate || (gst as any).rate_percent || 0
        return sum + rate
      }, 0)
      const taxableAmount = Number(item.taxable_amount || 0)
      
      if (totalRate > 0) {
        // Round rate to nearest 0.5 for grouping (to handle 2.5%, 5%, etc.)
        const roundedRate = Math.round(totalRate * 2) / 2
        if (!taxBreakdown[roundedRate]) {
          taxBreakdown[roundedRate] = { amount: 0, taxableAmount: 0 }
        }
        taxBreakdown[roundedRate].amount += taxAmount
        taxBreakdown[roundedRate].taxableAmount += taxableAmount
      }
    } else if (taxAmount > 0 && item.tax_rate_id) {
      // If no GST breakdown but we have tax, estimate rate
      const taxableAmount = Number(item.taxable_amount || 0)
      if (taxableAmount > 0) {
        const totalRate = (taxAmount / taxableAmount) * 100
        const roundedRate = Math.round(totalRate * 2) / 2
        if (!taxBreakdown[roundedRate]) {
          taxBreakdown[roundedRate] = { amount: 0, taxableAmount: 0 }
        }
        taxBreakdown[roundedRate].amount += taxAmount
        taxBreakdown[roundedRate].taxableAmount += taxableAmount
      }
    }
  })
  
  // Use the actual invoice total from backend (with rounding applied)
  // If backend doesn't have total_amount, calculate it including delivery charge
  const total = Number(invoice.total_amount || (grandTotal + deliveryCharge))
  
  // Create the full Printer element with its content
  const receipt = (
    <Printer type={printerType} width={width}>
      {/* Company Header */}
      <Text align="center" bold>
        {company.name.toUpperCase()}
      </Text>
      {displayAddress1 && <Text align="center">{displayAddress1}</Text>}
      {displayAddress2 && <Text align="center">{displayAddress2}</Text>}
      {displayPhone && <Text align="center">Ph No.: {displayPhone}</Text>}
      {displayGstin && <Text align="center">GSTIN: {displayGstin}</Text>}
      
      <Line character="-" />
      
      {/* Invoice Header */}
      <Text align="center" bold>Invoice</Text>
      <Text align="center" bold>{party?.name?.toUpperCase() || 'CASH'}</Text>
      
      <Text>Bill To:</Text>
      {party?.name && <Text>{party.name.toUpperCase()}</Text>}
      {party?.billing_address && <Text>{party.billing_address}</Text>}
      {party?.phone && <Text>Ph: {party.phone}</Text>}
      
      <Row left="" right={`Date: ${format(new Date(invoice.transaction_date), 'dd/MM/yyyy')}`} />
      <Row left="" right={`Invoice No: ${invoice.voucher_type?.prefix || 'FY'}${format(new Date(), 'yy')}-${invoice.voucher_number}`} />
      
      <Line character="-" />
      
      {/* Items Header */}
      <Row left="# Name" center="Qty" right="Price Amount" />
      <Line character="-" />
      
      {/* Sort items alphabetically by name */}
      {[...(invoice.items || [])].sort((a, b) => {
        const nameA = (a.item?.name || a.name || a.description || 'Item').toUpperCase()
        const nameB = (b.item?.name || b.name || b.description || 'Item').toUpperCase()
        return nameA.localeCompare(nameB)
      }).map((item, index) => {
        const quantity = Number(item.quantity || 0)
        const rate = Number(item.rate || 0)
        // Use the actual amount from item which includes tax calculations
        const amount = Number(item.total_amount || item.amount || 0)
        const itemName = item.item?.name || item.name || item.description || 'Item'
        // Tamil name from additional_fields
        const tamilName = item.item?.additional_fields?.name_tamil || ''
        // FSSI number might be in description or additional fields
        const fssiNumber = item.description?.includes('FSSI') ? item.description : ''
        
        // Calculate space for item name (leaving room for right-aligned columns)
        const rightColumns = `${quantity.toString().padStart(3)} ${rate.toFixed(2).padStart(7)} ${amount.toFixed(2).padStart(7)}`
        const itemNameSpace = width - rightColumns.length - 3 // 3 for index and space
        
        // Wrap item name if it's too long
        const fullItemName = `${index + 1} ${itemName.toUpperCase()}`
        const wrappedLines: string[] = []
        
        if (fullItemName.length <= itemNameSpace) {
          wrappedLines.push(fullItemName)
        } else {
          let currentLine = fullItemName.substring(0, itemNameSpace)
          let remainder = fullItemName.substring(itemNameSpace)
          wrappedLines.push(currentLine)
          
          while (remainder.length > 0) {
            currentLine = '  ' + remainder.substring(0, itemNameSpace - 2)
            remainder = remainder.substring(itemNameSpace - 2)
            wrappedLines.push(currentLine.trimEnd())
          }
        }
        
        return (
          <React.Fragment key={item.id || index}>
            {/* First line with item name and amounts */}
            <Row
              left={wrappedLines[0]}
              right={rightColumns}
            />
            {/* Additional wrapped lines */}
            {wrappedLines.slice(1).map((line, lineIndex) => (
              <Text key={`${index}-wrap-${lineIndex}`}>{line}</Text>
            ))}
            {/* Tamil name on new line if present */}
            {tamilName && (() => {
              const tamilDisplay = `  (${tamilName})`
              if (tamilDisplay.length <= width) {
                return <Text>{tamilDisplay}</Text>
              } else {
                // Wrap Tamil name if too long
                const tamilLines: string[] = []
                let currentLine = tamilDisplay.substring(0, width)
                tamilLines.push(currentLine)
                let remainder = tamilDisplay.substring(width)
                while (remainder.length > 0) {
                  currentLine = '  ' + remainder.substring(0, width - 2)
                  remainder = remainder.substring(width - 2)
                  tamilLines.push(currentLine.trimEnd())
                }
                return (
                  <>
                    {tamilLines.map((line, tamilIndex) => (
                      <Text key={`${index}-tamil-${tamilIndex}`}>{line}</Text>
                    ))}
                  </>
                )
              }
            })()}
            {/* FSSI number on new line if present */}
            {fssiNumber && <Text>{`  FSSI: ${fssiNumber}`}</Text>}
          </React.Fragment>
        )
      })}
      
      <Line character="-" />
      
      {/* Summary */}
      <Text>Total</Text>
      <Row left="" center={invoice.items?.reduce((sum, item) => sum + Number(item.quantity || 0), 0).toString() || '0'} right="" />
      
      <Row left="Sub Total" right={`: ${subtotal.toFixed(2)}`} />
      
      {/* Tax Breakdown */}
      {Object.entries(taxBreakdown).map(([rate, data]) => {
        const cgst = data.amount / 2
        const sgst = data.amount / 2
        const taxRate = Number(rate)/2
        return (
          <React.Fragment key={rate}>
            <Row left={`SGST @ ${taxRate}%`} right={`: ${sgst.toFixed(2)}`} />
            <Row left={`CGST @ ${taxRate}%`} right={`: ${cgst.toFixed(2)}`} />
          </React.Fragment>
        )
      })}
      
      {totalDiscount > 0 && (
        <Row left="Discount" right={`: -${totalDiscount.toFixed(2)}`} />
      )}
      
      {deliveryCharge > 0 && (
        <Row left="Delivery Charge" right={`: ${deliveryCharge.toFixed(2)}`} />
      )}
      
      <Row left="Total" right={`: ${total.toFixed(2)}`} />
      
      <Line character="-" />
      
      {/* Party Closing Balance */}
      {closingBalance && party && (
        <>
          <Text>{'\n'}</Text>
          <Row 
            left="Balance" 
            right={`: ₹${parseFloat(closingBalance.closing_balance).toFixed(2)} ${closingBalance.closing_balance_type}`} 
          />
          <Text>{'\n'}</Text>
        </>
      )}
      
      {/* Footer */}
      <Text>Terms & Conditions</Text>
      <Text>Thanks for doing business with us!</Text>
      
      <Text>{'\n\n\n'}</Text>
      
      <Cut />
    </Printer>
  )
  
  const data = await render(receipt)
  return data
}